import { useCallback, useEffect, useState } from 'react'
import { CurieSelect } from '~/components/common/CurieSelect'
import { SimpleTextarea } from '~/components/common/SimpleTextarea'
import {
  AgentRequest,
  IntegrationPlatform,
  integrationPlatformFromJSON,
  integrationPlatformToJSON,
  MigrationDocumentInput
} from '~/protos/agents'
import { bridge } from '~/services/bridge'
import {
  buildCommandContextUploadFromApiItems,
  type ContextUploadApiItem
} from '~/types/contextUpload'
import { getIntegrationPlatformItems } from '~/utils/agentUtils'
import { TaskParams } from './BaseTaskForm'
import {
  SupportingFilesUpload,
  type SupportingFilesUploadState
} from './shared/SupportingFilesUpload'

interface GenerateMigrationDocFormProps {
  onChange: (task: TaskParams) => void
  withExamplePrefilled?: boolean
  initialTaskData?: TaskParams | null
}

export function GenerateMigrationDocForm({
  onChange,
  withExamplePrefilled,
  initialTaskData
}: GenerateMigrationDocFormProps) {
  // Initialize from initialTaskData if editing, otherwise use example or empty
  const getInitialEndpoint = () => {
    if (initialTaskData?.request?.migrationDocInput?.endpointDesc) {
      return initialTaskData.request.migrationDocInput.endpointDesc
    }
    return withExamplePrefilled ? 'POST /update-user-flow' : ''
  }

  const getInitialPlatform = () => {
    if (initialTaskData?.request?.migrationDocInput?.platform) {
      return initialTaskData.request.migrationDocInput.platform
    }
    return IntegrationPlatform.TIBCO
  }

  const isPlugin = bridge.isInPlugin()
  const [integrationPlatform, setIntegrationPlatform] =
    useState<IntegrationPlatform>(getInitialPlatform())
  const [endpoint, setEndpoint] = useState<string>(getInitialEndpoint())
  const [contextUploadData, setContextUploadData] = useState<ContextUploadApiItem[]>([])
  const [submitBlockedReason, setSubmitBlockedReason] = useState<string | undefined>()

  // Update state when initialTaskData changes (e.g., when editing different tasks)
  useEffect(() => {
    if (initialTaskData) {
      const platform =
        initialTaskData.request?.migrationDocInput?.platform ?? IntegrationPlatform.TIBCO
      const details = initialTaskData.request?.migrationDocInput?.endpointDesc ?? ''
      setIntegrationPlatform(platform)
      setEndpoint(details)
    }
  }, [initialTaskData])

  // Update endpoint when withExamplePrefilled changes
  useEffect(() => {
    if (!initialTaskData) {
      setEndpoint(withExamplePrefilled ? 'POST /update-user-flow' : '')
    }
  }, [withExamplePrefilled, initialTaskData])

  const handleSupportingFilesChange = useCallback(
    ({ contextUploadData, hasPendingUploads, hasFailedUploads }: SupportingFilesUploadState) => {
      setContextUploadData(contextUploadData)
      setSubmitBlockedReason(
        hasPendingUploads
          ? 'Please wait for all supporting files to finish uploading.'
          : hasFailedUploads
            ? 'Some supporting file uploads failed. Please remove them and try again.'
            : undefined
      )
    },
    []
  )

  useEffect(() => {
    if (integrationPlatform != null && endpoint != null) {
      const commandString = `Endpoint: ${endpoint}\n\nPlatform: ${integrationPlatformToJSON(integrationPlatform)}`
      const request = AgentRequest.fromPartial({
        command: {
          commandString,
          contextUpload: buildCommandContextUploadFromApiItems(contextUploadData)
        },
        migrationDocInput: MigrationDocumentInput.fromPartial({
          platform: integrationPlatform,
          endpointDesc: endpoint
        })
      })
      onChange?.({ request, submitBlockedReason })
    }
  }, [contextUploadData, endpoint, integrationPlatform, onChange, submitBlockedReason])

  return (
    <div className='input-output-form'>
      <div className='description'>
        <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px] select-none'>
          Source Integration Platform:
        </div>
        <CurieSelect
          placeholder='Select source platform'
          className={isPlugin ? 'w-full pb-0' : 'w-64 pb-0'}
          selectedKeys={[integrationPlatformToJSON(integrationPlatform)]}
          items={getIntegrationPlatformItems(['FLEX_GATEWAY_RUST_POLICY'])}
          onSelectionChange={selectedKeys => {
            if (typeof selectedKeys === 'string' || selectedKeys.size === 0) {
              return
            }
            const key = selectedKeys.values().next().value as string
            setIntegrationPlatform(integrationPlatformFromJSON(key))
          }}
          disabled={withExamplePrefilled}
        />
      </div>
      <div className='description'>
        <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px] select-none'>
          Details:
        </div>
        <SimpleTextarea
          className='mx-0'
          onChange={setEndpoint}
          minRows={2}
          value={withExamplePrefilled ? endpoint : undefined}
          defaultValue={withExamplePrefilled ? undefined : getInitialEndpoint()}
          placeholder={`
Provide details about the endpoints, operations, or event triggers for which you need a migration document.
Examples:

Generate for all SOAP operations
Generate for the GetWeather SOAP operation
Generate for the GET /order/{orderId} endpoint
Generate for the order-consumer event listener
`.trim()}
          disabled={withExamplePrefilled}
        />
        <div className='mt-4'>
          <SupportingFilesUpload
            onChange={handleSupportingFilesChange}
            disabled={withExamplePrefilled}
          />
        </div>
      </div>
    </div>
  )
}
